import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { ScopedToast } from '@/hooks/useScopedToast';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import type { useThreePlBillLines } from '../hooks/useReportingV2';
import { formatUsageDay, type BillExtraRowView } from '../lib/three-pl-usage-view';

type RemoveLineMutation = ReturnType<typeof useThreePlBillLines>['removeLine'];

const QTY_KEYS: Record<BillExtraRowView['chargePer'], string> = {
  piece: 'reporting.threePlUsage.count.pieces',
  order: 'reporting.threePlUsage.count.orders',
  flat: 'reporting.threePlUsage.count.times',
};

export interface RemoveBillLineDialogProps {
  /** The hand-added line to remove; null = closed. */
  row: BillExtraRowView | null;
  clientName: string;
  /** Formats in the report's currency (D2): the amount the bill goes down by. */
  formatValue: (value: number) => string;
  /**
   * The tab's scoped toast (`useScopedToast`): every outcome goes through it,
   * and it alone decides what closes with the tab (an error never does).
   */
  notify: ScopedToast;
  /** The tab's mutation (useThreePlBillLines): one call, then one report refetch. */
  removeLine: RemoveLineMutation;
  onClose: () => void;
  /**
   * Where focus goes once the alert is gone. `gone` is true when the line is
   * off the bill (removed now, or already removed), so its row and its remove
   * button no longer exist.
   */
  onCloseAutoFocus: (event: Event, gone: boolean) => void;
}

/**
 * Remove a hand-added line (D3, owner `skus.costs_manage` holders only). It
 * names the service, the quantity, the day it bills on and who stops seeing
 * it. Keep it holds the
 * focus; Remove is the orange outline of a reversible action (DESIGN.md
 * §13.6: the 3PL can add it again). ONE `deleteThreePlBillLine` call; a line
 * someone else already removed reads as removed, not as a failure. A line with
 * an amount above 0 also says how much the bill goes down by (never "$0.00").
 */
export function RemoveBillLineDialog({
  row,
  clientName,
  formatValue,
  notify,
  removeLine,
  onClose,
  onCloseAutoFocus,
}: RemoveBillLineDialogProps) {
  const { t, i18n } = useTranslation('inventory');
  const busy = removeLine.isPending;
  const goneRef = useRef(false);
  // Keeps the words drawn while the alert fades out.
  const shownRef = useRef<BillExtraRowView | null>(null);
  if (row !== null) shownRef.current = row;
  const shown = shownRef.current;
  let body: string | null = null;
  if (shown) {
    const params = {
      service: shown.serviceName,
      qty: t(QTY_KEYS[shown.chargePer], { count: shown.qty }),
      date: shown.dateRange ? formatUsageDay(shown.dateRange.from, i18n.language) : '',
      client: clientName,
    };
    // The amount only when there is one: no price, or a deliberate 0, keeps
    // the plain sentence, so the alert never says "goes down by $0.00".
    body =
      shown.amount > 0
        ? t('reporting.threePlUsage.delete.bodyAmount', { ...params, amount: formatValue(shown.amount) })
        : t('reporting.threePlUsage.delete.body', params);
  }

  const confirm = () => {
    const target = row;
    if (!target?.lineId || busy) return;
    removeLine.mutate(
      { lineId: target.lineId },
      {
        onSuccess: () => {
          goneRef.current = true;
          onClose();
          notify({ title: t('reporting.threePlUsage.toast.removed') });
        },
        onError: (error) => {
          if (trpcErrorCode(error) === 'NOT_FOUND') {
            goneRef.current = true;
            onClose();
            notify({ title: t('reporting.threePlUsage.toast.alreadyRemoved') });
            return;
          }
          onClose();
          notify({ variant: 'destructive', title: t('reporting.threePlUsage.toast.removeError'), duration: Infinity });
        },
      },
    );
  };

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <AlertDialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          const gone = goneRef.current;
          goneRef.current = false;
          onCloseAutoFocus(event, gone);
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t('reporting.threePlUsage.delete.title')}</AlertDialogTitle>
          <AlertDialogDescription className="[overflow-wrap:anywhere]">
            {body}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-8 px-3 text-xs" disabled={busy}>
            {t('reporting.threePlUsage.delete.cancel')}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="outline"
            className="h-8 gap-1.5 border-orange-300 px-3 text-xs text-orange-700 hover:bg-orange-50 hover:text-orange-800"
            onClick={confirm}
            disabled={busy}
            aria-busy={busy}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {t('reporting.threePlUsage.delete.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
